import { Admission } from '../services/httpServices/ipdService';
import { PatientPrintHospital } from './patientPrint';
import { joinFullName } from './patientName';
import { patientCategoryLabel } from './patientCategory';
import {
  composePatientAddress,
  patientAddressDetailRows,
  permanentAddressParts,
  presentAddressParts,
} from './patientAddress';

function escapeHtml(value: unknown): string {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Short, human-friendly reference for an admission. Admissions have no dedicated
 * number, so the first block of the UUID is used (matching the ID shown on the
 * admission page). Discharge papers use the same id with a `DIS` prefix.
 */
export function admissionReference(
  admission: Pick<Admission, 'id'>,
  prefix = 'ADM',
): string {
  const id = String(admission?.id || '').replace(/-/g, '');
  return id ? `${prefix}-${id.slice(0, 8).toUpperCase()}` : '—';
}

type ConfirmationKind = 'admission' | 'discharge';

/**
 * Builds a self-contained, print-ready confirmation document for an inpatient
 * admission. `kind = 'discharge'` produces a Discharge Confirmation (for
 * discharged patients); otherwise an Admission Confirmation. Used by the
 * Admission Details print action (browser print dialog).
 */
function buildConfirmationHtml(
  admission: Admission,
  hospital: PatientPrintHospital,
  kind: ConfirmationKind,
): string {
  const esc = (value: unknown) => escapeHtml(value);
  const dash = (value: unknown) => {
    const text = value === null || value === undefined ? '' : String(value).trim();
    return text.length > 0 ? esc(text) : '—';
  };
  const fmtDateTime = (value?: string | Date | null) => {
    if (!value) return '—';
    const date = new Date(value);
    if (isNaN(date.getTime())) return '—';
    return date.toLocaleString('en-GB', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };
  const money = (value: unknown) => {
    const amount = Number(value);
    if (!Number.isFinite(amount)) return '৳0';
    return `৳${amount.toLocaleString('en-BD', { maximumFractionDigits: 2 })}`;
  };
  const fmtDate = (value?: string | Date | null) => {
    if (!value) return '—';
    const date = new Date(value);
    if (isNaN(date.getTime())) return '—';
    return date.toLocaleDateString('en-GB', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  };

  const isDischarge = kind === 'discharge';
  // A cancelled stay is printed with the admission kind: there was no discharge.
  const isCancelled =
    String(admission.status ?? '').toUpperCase() === 'CANCELLED';
  const cancelledAt = isCancelled ? admission.updatedAt : null;
  const title = isDischarge
    ? 'DISCHARGE CONFIRMATION'
    : isCancelled
      ? 'CANCELLED ADMISSION RECORD'
      : 'ADMISSION CONFIRMATION';
  const reference = admissionReference(admission, isDischarge ? 'DIS' : 'ADM');

  const patient = admission.patient || {};
  const bed = admission.bed || {};
  const fullName =
    joinFullName(patient) ||
    patient.registrationNumber ||
    'Unknown patient';

  const ageGender = [
    patient.age ? `${patient.age} yrs` : '',
    patient.gender ? String(patient.gender).replace(/^\w/, (c) => c.toUpperCase()) : '',
  ]
    .filter(Boolean)
    .join(' / ');

  const permanentParts = permanentAddressParts(patient);
  const permanentAddress = composePatientAddress(permanentParts);
  const presentParts = patient.sameAsPermanent ? permanentParts : presentAddressParts(patient);
  const presentAddress = patient.sameAsPermanent
    ? 'Same as permanent address'
    : composePatientAddress(presentParts);
  const permanentDetailRows = patientAddressDetailRows(permanentParts);
  const presentDetailRows = patient.sameAsPermanent
    ? []
    : patientAddressDetailRows(presentParts);

  // Emergency admissions may have no bed yet; never print a bare dash for them.
  const isEmergencyAdmission =
    !admission.bed ||
    String(admission.admissionType ?? '').toUpperCase() === 'EMERGENCY';
  const wardName =
    bed.wardEntity?.wardName || bed.ward || (isEmergencyAdmission ? 'Emergency' : '—');
  const bedLabel = bed.bedNumber || (isEmergencyAdmission ? 'No bed assigned' : '—');
  const admittedAt = admission.admittedAt || admission.admissionDate;
  const reason = admission.admissionReason || admission.diagnosis;

  // `dischargeDueAmount` is the snapshot taken when the patient left. The live
  // invoice balance can settle afterwards, so a reprint must not claim money is
  // still owed once it has been collected.
  const carriedDue = Number(admission.dischargeDueAmount || 0);
  // Cancelling writes the unpaid part of the bill off to the Risk Fund; the
  // paper must state that amount so the copy is not read as "fully paid".
  const riskFundWrittenOff = isCancelled
    ? Math.max(
        0,
        Math.round(
          ((Number(admission.totalAmount) || 0) - (Number(admission.paidAmount) || 0)) *
            100,
        ) / 100,
      )
    : 0;
  const liveDueRaw = Number(admission.dueAmount);
  const outstandingDue = Number.isFinite(liveDueRaw) ? Math.max(0, liveDueRaw) : carriedDue;
  const carriedDueSettled = carriedDue > 0 && liveDueRaw === 0;

  const lengthOfStay = (() => {
    if (!isDischarge) return null;
    const start = admittedAt ? new Date(admittedAt) : null;
    const end = admission.dischargedAt ? new Date(admission.dischargedAt) : null;
    if (!start || !end || isNaN(start.getTime()) || isNaN(end.getTime())) return null;
    const days = Math.max(1, Math.ceil((end.getTime() - start.getTime()) / 86_400_000));
    return `${days} day${days === 1 ? '' : 's'}`;
  })();

  const contact = [
    hospital.hotline ? `Hotline: ${hospital.hotline}` : '',
    hospital.email,
    hospital.website,
  ]
    .filter(Boolean)
    .join(' · ');

  // Signature/seal are shared with invoices and only shown when allowed in
  // General Settings → Documents.
  const showDocSignature = Boolean(
    hospital.showSignature && (hospital.signature || hospital.seal || hospital.signatoryName),
  );

  const row = (label: string, value: unknown) =>
    `<tr><th>${esc(label)}</th><td>${dash(value)}</td></tr>`;
  /** Like {@link row} but keeps a pre-built, already-escaped HTML fragment intact. */
  const rawRow = (label: string, html: string) =>
    `<tr><th>${esc(label)}</th><td>${html}</td></tr>`;

  const admissionRows = [
    rawRow(
      'Status',
      `<span class="badge ${
        isDischarge ? 'discharged' : isCancelled ? 'cancelled' : ''
      }">${esc(String(admission.status || '').replace(/_/g, ' '))}</span>`,
    ),
    isCancelled
      ? rawRow(
          'Cancelled On',
          `<span class="danger">${fmtDateTime(cancelledAt)}</span>`,
        )
      : '',
    isCancelled && riskFundWrittenOff > 0
      ? rawRow(
          'Written Off to Risk Fund',
          `<span class="warning">${money(riskFundWrittenOff)}</span>`,
        )
      : '',
    row('Admission Type', String(admission.admissionType || '').replace(/_/g, ' ')),
    isEmergencyAdmission
      ? rawRow(
          'Emergency',
          `<span class="badge emergency">${
            admission.bed ? 'Yes' : 'Yes — no bed assigned'
          }</span>`,
        )
      : '',
    row('Ward', wardName),
    row('Bed', bedLabel),
    bed.price ? row('Bed Charge', `${money(bed.price)} / night`) : '',
    row('Admitting Doctor', admission.doctor?.name),
    row('Assigned Nurse', admission.nurse?.name),
    admission.referredById || admission.referredBy ? row('Referred By', admission.referredBy?.name) : '',
    row('Admitted On', fmtDateTime(admittedAt)),
    isDischarge ? row('Discharge Initiated On', fmtDateTime(admission.dischargeInitiatedAt)) : '',
    isDischarge ? row('Discharged On', fmtDateTime(admission.dischargedAt)) : '',
    isDischarge ? row('Length of Stay', lengthOfStay) : '',
    isDischarge ? row('Discharge Type', String(admission.dischargeType || '').replace(/_/g, ' ')) : '',
    isDischarge && carriedDue > 0
      ? row(
          carriedDueSettled ? 'Balance at Discharge' : 'Balance Due at Discharge',
          carriedDueSettled ? `${money(carriedDue)} — settled` : money(carriedDue),
        )
      : '',
    isDischarge && carriedDue > 0 && !carriedDueSettled && outstandingDue < carriedDue
      ? row('Balance Still Due', money(outstandingDue))
      : '',
    isDischarge && carriedDue > 0 && !carriedDueSettled && admission.dischargeDueDate
      ? row('Payment Promised By', fmtDate(admission.dischargeDueDate))
      : '',
    row('Advance Paid', money(admission.advancePaid)),
  ]
    .filter(Boolean)
    .join('');

  const empty = (text: string) => `<div class="muted">${esc(text)}</div>`;

  const reasonSection = `
    <div>
      <h2 class="section">Reason / Diagnosis</h2>
      ${reason ? `<div class="note">${esc(reason)}</div>` : empty('Not recorded.')}
    </div>
    <div>
      <h2 class="section">Referral Details</h2>
      ${
        admission.referralDetails
          ? `<div class="note">${esc(admission.referralDetails)}</div>`
          : empty('Not applicable.')
      }
    </div>`;

  const dischargeSection = isDischarge
    ? `
  <h2 class="section">Discharge Summary</h2>
  ${
    admission.dischargeSummary
      ? `<div class="note">${esc(admission.dischargeSummary)}</div>`
      : empty('Not recorded.')
  }

  <h2 class="section">Discharge Instructions</h2>
  ${
    admission.dischargeInstructions
      ? `<div class="note">${esc(admission.dischargeInstructions)}</div>`
      : empty('No specific instructions were recorded.')
  }

  ${
    carriedDue > 0
      ? carriedDueSettled
        ? `<div class="note"><strong>Balance at discharge: ${money(
            carriedDue,
          )}.</strong> This amount was settled after discharge; nothing is owed on this visit.</div>`
        : `<div class="note"><strong>Balance due at discharge: ${money(
            carriedDue,
          )}.</strong> ${
            outstandingDue < carriedDue
              ? `Of this, ${money(outstandingDue)} is still unpaid. `
              : ''
          }This amount remains on the patient's bill and can be settled at the billing counter${
            admission.dischargeDueDate
              ? ` by ${fmtDate(admission.dischargeDueDate)}`
              : ''
          }.</div>`
      : ''
  }`
    : '';

  const declaration = isCancelled
    ? `This document records that the inpatient admission above was cancelled${
        cancelledAt ? ` on ${fmtDateTime(cancelledAt)}` : ''
      }. No services are chargeable against this stay after cancellation${
        riskFundWrittenOff > 0
          ? `, and the unpaid balance of ${money(riskFundWrittenOff)} was written off to the Risk Fund`
          : ''
      }.`
    : isDischarge
      ? `This document confirms that the above patient has been discharged from
    ${esc(hospital.name)}${admission.dischargedAt ? ` on ${fmtDateTime(admission.dischargedAt)}` : ''}.
    Please retain it for your records and follow the discharge instructions above.
    For follow-up or emergencies, contact the hospital using the details in the header.`
      : `This document confirms that the above patient has been admitted as an inpatient
    at ${esc(hospital.name)}. Please present it at the reception, nursing station and
    billing counter as required, and keep it for your records.`;

  return `<!doctype html>
<html>
<head>
<meta charset="utf-8" />
<title>${esc(title)} ${esc(reference)}</title>
<style>
  @font-face { font-family: 'SutonnyMJ'; src: url('/fonts/SutonnyMJ.ttf') format('truetype'); font-display: swap; }
  .bn { font-family: 'SutonnyMJ', 'Noto Sans Bengali', 'Arial Unicode MS', Arial, sans-serif; }
  * { box-sizing: border-box; }
  /* Zero page margin suppresses Chrome's date/title header; the page-shell
     wrapper repeats the visual margin on every printed page instead. */
  @page { size: A4; margin: 0; }
  body { font-family: Arial, Helvetica, sans-serif; color: #111827; margin: 0; padding: 0; font-size: 13px; }
  .page-shell { padding: 12mm 10mm; -webkit-box-decoration-break: clone; box-decoration-break: clone; }
  .header { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 2px solid #1f2937; padding-bottom: 12px; }
  .hospital { display: flex; gap: 12px; align-items: flex-start; }
  .hospital img { height: 56px; width: 56px; object-fit: contain; }
  .hospital h1 { margin: 0 0 4px; font-size: 20px; }
  .hospital p { margin: 0; font-size: 12px; color: #4b5563; }
  .title { text-align: right; }
  .title h2 { margin: 0; font-size: 20px; letter-spacing: 2px; }
  .title p { margin: 2px 0 0; font-size: 12px; color: #4b5563; }
  .title .ref { font-family: 'Courier New', monospace; font-weight: 700; color: #111827; }
  h2.section { font-size: 13px; text-transform: uppercase; letter-spacing: 1px; color: #374151; margin: 22px 0 8px; padding-bottom: 4px; border-bottom: 1px solid #e5e7eb; }
  tr.subhead-row th { padding-top: 12px; font-size: 11px; text-transform: uppercase; letter-spacing: 0.8px; color: #6b7280; border-bottom: 0; }
  table { width: 100%; border-collapse: collapse; }
  table th { text-align: left; width: 34%; padding: 6px 8px; vertical-align: top; color: #6b7280; font-weight: 500; border-bottom: 1px solid #f3f4f6; }
  table td { padding: 6px 8px; vertical-align: top; border-bottom: 1px solid #f3f4f6; }
  .uhid { font-family: 'Courier New', monospace; font-weight: 700; }
  .badge { display: inline-block; padding: 2px 10px; border-radius: 999px; font-size: 11px; font-weight: 700; background: #dbeafe; color: #1d4ed8; }
  .badge.discharged { background: #dcfce7; color: #15803d; }
  .badge.cancelled { background: #fee2e2; color: #b91c1c; }
  .badge.emergency { background: #fef3c7; color: #b45309; }
  .danger { color: #b91c1c; font-weight: 700; }
  .warning { color: #b45309; font-weight: 700; }
  .two-col { display: flex; gap: 24px; }
  .two-col > div { flex: 1; }
  .note { margin-top: 8px; padding: 10px; border: 1px solid #e5e7eb; border-radius: 6px; background: #f9fafb; white-space: pre-wrap; }
  .declaration { margin-top: 18px; padding: 10px 12px; border: 1px dashed #d1d5db; border-radius: 6px; color: #4b5563; font-size: 12px; }
  .signatures { display: flex; justify-content: space-between; gap: 40px; margin-top: 48px; }
  .signatures .sig { flex: 1; text-align: center; font-size: 11px; color: #4b5563; }
  .signatures .sig-graphic { position: relative; height: 44px; }
  .signatures .sig-img { position: absolute; bottom: 0; left: 50%; transform: translateX(-50%); height: 44px; max-width: 160px; object-fit: contain; }
  .signatures .seal-img { position: absolute; bottom: 0; right: 6px; height: 52px; width: 52px; object-fit: contain; opacity: .85; }
  .signatures .sig-rule { border-top: 1px solid #9ca3af; padding-top: 6px; }
  .signatures .sig-name { margin-top: 2px; font-size: 10px; color: #6b7280; }
  .footer { margin-top: 30px; border-top: 1px solid #e5e7eb; padding-top: 10px; font-size: 11px; color: #6b7280; text-align: center; break-inside: avoid; }
  .muted { color: #9ca3af; }
  @media print { body { padding: 0; } h2.section { page-break-after: avoid; } }
</style>
</head>
<body>
  <div class="page-shell">
  <div class="header">
    <div class="hospital">
      ${hospital.logo ? `<img src="${esc(hospital.logo)}" alt="Logo" />` : ''}
      <div>
        <h1>${esc(hospital.name)}</h1>
        ${hospital.nameBn ? `<p class="bn">${esc(hospital.nameBn)}</p>` : ''}
        ${hospital.address ? `<p>${esc(hospital.address)}</p>` : ''}
        ${contact ? `<p>${esc(contact)}</p>` : ''}
      </div>
    </div>
    <div class="title">
      <h2>${esc(title)}</h2>
    </div>
  </div>

  <h2 class="section">Patient</h2>
  <table>
    ${row('Name', fullName)}
    ${row('ID / Registration No.', patient.registrationNumber)}
    ${row('Age / Gender', ageGender)}
    ${row('Blood Group', patient.bloodGroup)}
    ${row('Mobile', patient.mobileNumber)}
    ${row('Category', patientCategoryLabel(patient.patientCategory))}
    <tr class="subhead-row"><th colspan="2">Permanent Address</th></tr>
    ${row('Address', permanentAddress)}
    ${permanentDetailRows.map((entry) => row(entry.label, entry.value)).join('')}
    <tr class="subhead-row"><th colspan="2">Present Address</th></tr>
    ${
      patient.sameAsPermanent
        ? row('Address', 'Same as permanent address')
        : [row('Address', presentAddress), ...presentDetailRows.map((entry) => row(entry.label, entry.value))].join('')
    }
    ${row('Guardian', [patient.guardianName, patient.guardianRelation].filter(Boolean).join(' · '))}
    ${row('Guardian Phone', patient.guardianPhone)}
    ${row('Emergency Contact', [patient.emergencyContactName, patient.emergencyContactPhone].filter(Boolean).join(' · '))}
  </table>

  <h2 class="section">${isDischarge ? 'Admission & Discharge' : 'Admission'}</h2>
  <table>
    ${admissionRows}
  </table>
  ${dischargeSection}

  <div class="two-col">
    ${reasonSection}
  </div>

  <div class="declaration">
    ${declaration}
  </div>

  <div class="signatures">
    <div class="sig">
      <div class="sig-graphic"></div>
      <div class="sig-rule">Patient / Attendant Signature</div>
    </div>
    <div class="sig">
      <div class="sig-graphic">
        ${showDocSignature && hospital.seal ? `<img class="seal-img" src="${esc(hospital.seal)}" alt="Hospital seal" />` : ''}
        ${showDocSignature && hospital.signature ? `<img class="sig-img" src="${esc(hospital.signature)}" alt="Authorized signature" />` : ''}
      </div>
      <div class="sig-rule">Authorized Signature</div>
      ${showDocSignature && hospital.signatoryName ? `<div class="sig-name">${esc(hospital.signatoryName)}</div>` : ''}
    </div>
  </div>

  <div class="footer">
    ${hospital.name ? `<div>${esc(hospital.name)}</div>` : ''}
    ${hospital.certified ? `<div>${esc(hospital.certified)}</div>` : ''}
    <div>Generated on ${fmtDateTime(new Date())}</div>
  </div>
  </div>
</body>
</html>`;
}

/** Admission Confirmation paper for an admitted (in-house) patient. */
export function buildAdmissionConfirmationHtml(
  admission: Admission,
  hospital: PatientPrintHospital,
): string {
  return buildConfirmationHtml(admission, hospital, 'admission');
}

/** Discharge Confirmation paper for a discharged patient. */
export function buildDischargeConfirmationHtml(
  admission: Admission,
  hospital: PatientPrintHospital,
): string {
  return buildConfirmationHtml(admission, hospital, 'discharge');
}
